import { useApolloClient } from '@apollo/client';
import { Box, FieldSet, Gleam, Label, LabeledControl, Legend, Radio, VisuallyHidden } from '@zillow/constellation-10';
import { useContext } from 'react';
import { ConversationList_GetConversationsDocument, ReadRepliedStatus } from '../../../../types/GraphqlTypes';
import { MAX_OLD_CONVERSATION_DAYS } from '../../../../constants/ConfigConstants';
import useInboxRouterHelper from '../../../../hooks/useInboxRouterHelper';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import { useSatelliteWebLandlordConversationHandler_getCachedConversationCounts } from '@zg-rentals/ts-satellite-client-react-query';
import { getCurrentSessionURL } from '../../../../utils/fullStory';

export default function StatusSelector() {
  const apolloClient = useApolloClient();
  const { changeFiltersOrFolders } = useInboxRouterHelper();
  const { clientTimeZone, daysBack, folder, listingAlias, isByProperty, readRepliedStatus, renterProgressStatus } =
    useContext(InboxPageContext);
  const fullStorySessionUrl = getCurrentSessionURL();

  const { data } = useSatelliteWebLandlordConversationHandler_getCachedConversationCounts({
    meta: {
      tags: ['satellite:conversationCounts'],
      context: {
        api: 'getCachedConversationCounts',
        details: 'StatusSelector - Error getting unread counts',
        fullStorySessionUrl,
      },
    },
  });

  const unreadConversationCount = data?.unreadCount || 0;
  const shouldShowUnreadCount = !isByProperty && unreadConversationCount > 0;

  const handleStatusChange = async (selectedStatus: ReadRepliedStatus) => {
    await apolloClient.query({
      query: ConversationList_GetConversationsDocument,
      variables: {
        listingAlias: listingAlias || null,
        clientTimeZone,
        daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
        folder,
        renterProgressStatus,
        readRepliedStatus: selectedStatus,
      },
      fetchPolicy: 'network-only',
    });

    await changeFiltersOrFolders({
      folder,
      readRepliedStatus: selectedStatus,
      renterProgressStatus,
      byProperty: isByProperty,
      listingAlias: listingAlias,
    });
  };

  const getOnChangeHandler = (selectedStatus: ReadRepliedStatus) => () => handleStatusChange(selectedStatus);

  return (
    <FieldSet
      legend={
        <VisuallyHidden>
          <Legend>Filter by conversation status</Legend>
        </VisuallyHidden>
      }
      css={{
        paddingLeft: 'tighter',
        paddingBottom: 'default',
      }}
    >
      <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <LabeledControl
          label={<Label>Show unread only</Label>}
          control={
            <Radio
              name="status-filter-option"
              checked={readRepliedStatus === ReadRepliedStatus.Unread}
              onChange={getOnChangeHandler(ReadRepliedStatus.Unread)}
            />
          }
        />
        {shouldShowUnreadCount && (
          <Box>
            <Gleam appearance="ellipsis" count={unreadConversationCount} maxCount={99} tone="default" />
          </Box>
        )}
      </Box>

      <LabeledControl
        label={<Label>Not replied to</Label>}
        control={
          <Radio
            name="status-filter-option"
            checked={readRepliedStatus === ReadRepliedStatus.NotRepliedTo}
            onChange={getOnChangeHandler(ReadRepliedStatus.NotRepliedTo)}
          />
        }
      />
    </FieldSet>
  );
}
